import { useEffect } from 'react';
import { UserCog } from 'lucide-react';
import { Input } from '@/core/ui/components';
import type { WizardFormData } from '../types';

interface AdminAccountStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  onValidChange: (isValid: boolean) => void;
  errors: Record<string, string>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AdminAccountStep({ formData, onChange, onValidChange, errors }: AdminAccountStepProps) {
  useEffect(() => {
    const isValid =
      formData.adminName.trim().length > 0 &&
      formData.adminEmail.trim().length > 0 &&
      EMAIL_REGEX.test(formData.adminEmail);
    onValidChange(isValid);
  }, [formData.adminName, formData.adminEmail, onValidChange]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <UserCog className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-theme">Admin Account</h3>
          <p className="text-sm text-theme-secondary">Set up the primary administrator for this tenant</p>
        </div>
      </div>

      <Input
        label="Admin Name"
        value={formData.adminName}
        onChange={(e) => onChange({ adminName: e.target.value })}
        error={errors.adminName}
        placeholder="John Doe"
        required
      />

      <Input
        label="Admin Email"
        type="email"
        value={formData.adminEmail}
        onChange={(e) => onChange({ adminEmail: e.target.value })}
        error={errors.adminEmail}
        placeholder="admin@example.com"
        required
      />

      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <p className="text-sm text-amber-700 dark:text-amber-300">
          An invitation email will be sent to this address with instructions to set up their password and access the platform.
        </p>
      </div>
    </div>
  );
}
